/**
 * Поле поиска с задержкой.
 *
 * §29.3 задаёт SEARCH_MIN_LENGTH = 2: запрос на один символ вернёт полбазы
 * и нагрузит сервер впустую. Ниже порога поиск просто не отправляется,
 * и пользователю об этом сказано.
 */

import { Search, X } from "lucide-react";
import { useEffect, useRef } from "react";

import { cn } from "./cn";
import { IconButton } from "./IconButton";

const MIN_LENGTH = 2;
const DEBOUNCE_MS = 300;

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Вызывается с задержкой и только при длине не меньше порога. */
  onSearch: (query: string) => void;
  placeholder?: string;
  label: string;
  className?: string;
}

export function SearchInput({
  value,
  onChange,
  onSearch,
  placeholder = "Поиск",
  label,
  className,
}: SearchInputProps) {
  // Признак вычисляется из значения, а не хранится в состоянии: хранить
  // то, что выводится из пропсов, значит держать две версии правды.
  const trimmed = value.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_LENGTH;

  // Колбэк держим в ссылке, а не в зависимостях эффекта.
  //
  // Вызывающий код почти всегда передаёт стрелку прямо в JSX, то есть новую
  // функцию на каждый рендер. Если она попадёт в зависимости, эффект будет
  // перезапускаться каждый рендер — а он умеет вызывать onSearch синхронно.
  // Когда onSearch меняет состояние (например, пишет фильтр в адрес),
  // получается бесконечный цикл рендеров.
  const onSearchRef = useRef(onSearch);
  // Обновление в эффекте, а не во время рендера: чтение и запись ref
  // при рендере — источник несогласованного состояния.
  useEffect(() => {
    onSearchRef.current = onSearch;
  });

  // Последнее отправленное значение: повторно отправлять то же самое
  // не нужно — это лишний запрос и лишняя запись в историю.
  //
  // Начальное значение считается уже отправленным: поле открывается с тем
  // поиском, что уже применён. Прежде пустое поле при монтировании сразу
  // слало onSearch(""), доска писала его в адрес, и каждый заход на неё
  // давал лишнюю навигацию и повторный рендер всей страницы.
  const lastEmitted = useRef<string | null>(trimmed);

  useEffect(() => {
    if (trimmed.length === 0) {
      if (lastEmitted.current !== "") {
        lastEmitted.current = "";
        onSearchRef.current("");
      }
      return;
    }
    if (trimmed.length < MIN_LENGTH) return;
    if (lastEmitted.current === trimmed) return;

    const timer = setTimeout(() => {
      lastEmitted.current = trimmed;
      onSearchRef.current(trimmed);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="relative">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-faint"
          aria-hidden
        />
        <input
          type="search"
          value={value}
          aria-label={label}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className={cn(
            "h-9 w-full rounded-md border border-line bg-surface pr-9 pl-9 text-sm text-fg",
            "placeholder:text-faint outline-none focus:border-accent",
            // Chrome и Edge рисуют у type="search" собственный крестик
            // очистки, и рядом с нашим их получалось два. Убираем
            // встроенный, а не свой: он не поддаётся оформлению, не
            // попадает в порядок обхода с клавиатуры и остаётся без
            // русской подписи — скринридер называет его по-английски
            // или не называет вовсе.
            "[&::-webkit-search-cancel-button]:hidden",
            "[&::-webkit-search-decoration]:hidden",
          )}
        />
        {value.length > 0 && (
          <IconButton
            icon={X}
            label="Очистить поиск"
            size="sm"
            onClick={() => onChange("")}
            className="absolute top-1/2 right-1.5 -translate-y-1/2"
          />
        )}
      </div>
      {tooShort && (
        <p className="text-[11px] text-faint">Введите не меньше {MIN_LENGTH} символов</p>
      )}
    </div>
  );
}
